import React, {useRef} from 'react';
import FeaturesState from '../../domain/FeaturesState';
import {connect} from 'react-redux';
import {
    isCreateObjectsVisible,
    creatingObjectGeometry,
    createdObjectWkt,
    createObjectTypeId,
    selectedFeatures,
    user,
    stateReferenceError, selectedFeatureType
} from '../../selectors';
import {
    checkValidRelationship,
    CheckValidRelationship,
    createFeature,
    CreateFeature, getWktFromPoints, GetWktFromPoints, GetWktFromReference, getWktFromReference,
    setCreateObjectTypeId,
    SetCreateObjectTypeId,
    setCreateObjectVisible, SetCreateObjectWkt, setCreateObjectWkt,
    setCreatingObjectGeometry,
    SetCreatingObjectGeometry,
    setReferenceError,
    SetReferenceError
} from '../../../../actions/actions';
import {Button, Checkbox, Modal, ModalBody, ModalFooter, ModalHeader, Radio} from 'react-bootstrap';
import datacatalog, {NvdbFeatureType} from '../../../../../integration/datacatalog/datacatalog-services';
import {DisplayFeature, DisplayFeatureType, GeometryType, UserSession} from '../../domain';
import Draggable from 'react-draggable';

type GeometryChoice = 'DRAW' | 'REFERENCE' | 'COPY';
const geometryChoices = {REFERENCE: () => 'Skriv inn vegsystemreferanse for objektet (kun Punkt og Linje)', COPY: (n: number) => 'Bruk geometrien fra ' + n + ' valgte punkter'};

interface CreateObjectStateProps {
    visible: boolean;
    createObjectTypeId: number | null;
    createGeometryType: GeometryType;
    createdWkt: string | null;
    catalog: datacatalog;
    selectedFeatureType: DisplayFeatureType;
    selectedFeatures: DisplayFeature[];
    referenceError: boolean;
    user: UserSession;
}

interface CreateObjectDispatchProps {
    createFeature: CreateFeature;
    fetchWktFromReference: GetWktFromReference;
    fetchWktFromSelection: GetWktFromPoints;
    checkRelationship: CheckValidRelationship;
    createGeometry: SetCreatingObjectGeometry;
    setTypeId: SetCreateObjectTypeId;
    setWkt: SetCreateObjectWkt;
    hide: () => void;
    setReferenseError: SetReferenceError;
}

type CreateObjectProps = CreateObjectStateProps &
    CreateObjectDispatchProps;

interface CreateObjectState {
    selectedGeometryMethod: GeometryChoice | null;
    searchString: string;
    reference: string;
    referenceEnd: string;
    name: string;
    localize: boolean;
    associate: boolean;
    validRelationship: boolean | 'loading';
}

const initState: CreateObjectState = {
    selectedGeometryMethod: null,
    searchString: '',
    reference: '',
    referenceEnd: '',
    name: '',
    localize: false,
    associate: false,
    validRelationship: false
};

class CreateObjectModal extends React.Component<CreateObjectProps, CreateObjectState> {

    constructor(props: CreateObjectProps) {
        super(props);
        this.clear = this.clear.bind(this);
        this.handleCreate = this.handleCreate.bind(this);
        this.handleConfirmGeometryChoice = this.handleConfirmGeometryChoice.bind(this);
        this.handleChooseObjectType = this.handleChooseObjectType.bind(this);
        this.handleGeometryChange = this.handleGeometryChange.bind(this);
        this.renderTypeChoice = this.renderTypeChoice.bind(this);
        this.renderGeometryChoice = this.renderGeometryChoice.bind(this);
        this.state = initState;
    }

    allTypes(wkt?: string | null): NvdbFeatureType[] {
        return this.props.catalog?.getFeatureTypes().filter(type => (!type.name.toLocaleLowerCase().includes('test') && type.id < 50000) && (wkt === null || wkt === undefined || type.geometryTypes.some(geoType => wkt.includes(geoType)))) ?? [];
    }

    selectedType(): NvdbFeatureType {
        if (this.props.selectedFeatureType) {
            return this.allTypes().find(type => this.props.selectedFeatureType.typeId === type.id);
        } else {
            return null;
        }
    }

    selectedObjectType() {
        if (this.props.createObjectTypeId === null) {
            return null;
        }
        return this.allTypes().find(type => type.id === this.props.createObjectTypeId) ?? null;
    }

    selectedTypeIdsFromSelectedFeatures() {
        const ids: number[] = [];

        for (const feature of this.props.selectedFeatures) {
            if (!ids.includes(feature.typeId)) {
                ids.push(feature.typeId);
            }
        }

        return ids;
    }

    selectedPointFeatureWktStrings() {
        const hasMoreThanOneInSelection = this.selectedTypeIdsFromSelectedFeatures().length > 1;
        if (hasMoreThanOneInSelection) {
            return [];
        }
        return this.props.selectedFeatures
            .map(feature => feature.geometry.wkt)
            .filter(wkt => wkt.includes('POINT'));
    }

    componentDidMount() {
        const selectedType = this.selectedType();
        if (selectedType) {
            this.handleChooseObjectType(selectedType.id, selectedType.name);
        }
    }

    componentDidUpdate(prevProps: Readonly<CreateObjectProps>, prevState: Readonly<CreateObjectState>, snapshot?: any) {
        if (!prevProps.visible && this.props.visible && this.props.createObjectTypeId === null) {
            const selectedType = this.selectedType();
            if (selectedType) {
                this.handleChooseObjectType(selectedType.id, selectedType.name);
            }
        }
        if (prevProps.createObjectTypeId !== null && this.props.createObjectTypeId === null) {
            this.setState({validRelationship: false});
        } else if (this.props.user?.hasPermission('ASSOCIATION:WRITE') &&
            (((this.props.createdWkt !== null
                && prevProps.createdWkt !== this.props.createdWkt)
                || (this.props.createObjectTypeId !== prevProps.createObjectTypeId))
                && this.state.selectedGeometryMethod === 'COPY'
                && this.state.validRelationship !== 'loading')) {
            this.setState({validRelationship: 'loading'});
            const parentTypeId = this.props.createObjectTypeId;
            const childrenTypeIds = this.selectedTypeIdsFromSelectedFeatures();
            if (parentTypeId && childrenTypeIds.length === 1) {
                this.props.checkRelationship(parentTypeId, childrenTypeIds[0], (valid) => this.setState({validRelationship: valid}));
            } else {
                this.setState({validRelationship: false});
            }
        }
    }

    clear() {
        this.setState(initState);
        this.props.hide();
        this.props.setTypeId(null);
        this.props.setWkt(null);
        this.props.createGeometry(null);
        this.props.setReferenseError(false);
    }

    handleCreate() {
        if (this.selectedObjectType() !== null && this.props.createdWkt !== null) {
            this.props.createFeature(this.selectedObjectType().id,
                this.state.name,
                this.props.createdWkt,
                this.state.localize,
                this.state.associate);
            this.clear();
        }
    }

    handleConfirmGeometryChoice(type: GeometryType) {
        if (this.selectedObjectType() !== null) {
            this.props.hide();
            this.props.createGeometry(type);
        }
    }

    handleChooseObjectType(id: number | null, name?: string) {
        const newState = {
            ...this.state,
            searchString: ''
        };
        if (name) {
            newState.name = name;
        }
        this.setState(newState);
        this.props.setTypeId(id);
    }

    renderTypeChoice() {
        if (this.selectedObjectType() === null) {
            const trimmedLowercaseSearch = this.state.searchString.trim().toLocaleLowerCase();
            return <>
                {
                    this.allTypes(this.props.createdWkt)
                        .filter(type => type.id.toString(10).includes(trimmedLowercaseSearch) || type.name.toLocaleLowerCase().includes(trimmedLowercaseSearch))
                        .map(type => {
                            const numGeoTypes = type.geometryTypes.length;
                            const disabled = numGeoTypes === 1 && type.geometryTypes.includes('POLYGON') || numGeoTypes === 0;
                            const title = disabled
                              ? numGeoTypes === 1
                                ? 'Oppretting av objekt med flategeometri støttes ikke'
                                : 'Oppretting av objekt uten egengeometri støttes ikke'
                              : '';
                            return (
                              <Radio key={type.id} name={'object-type'}
                                     onChange={() => this.handleChooseObjectType(type.id, type.name)}
                                     checked={this.selectedObjectType()?.id === type.id}
                                     disabled={disabled}
                                     title={title}
                              >
                                  <div title={title}>{type.id + ' - ' + type.name}</div>
                              </Radio>
                            );
                        })
                }
            </>;
        } else {
            return <div>
                <div className={'row full-width'}>
                    <label>Objekttype</label>
                    <p className={'content'}>{this.selectedObjectType().id} - {this.selectedObjectType().name}</p>
                    <div className={'action-icon'} title={'Endre objekttype'} onClick={() => this.handleChooseObjectType(null)}>
                        <i className={'fa fa-edit'} />
                    </div>
                </div>
                <div className={'row full-width'}>
                    <label htmlFor={'objectName'}>Objektnavn</label>
                    <input id={'objectName'} disabled={this.props.createObjectTypeId === null} value={this.state.name} onChange={(e) => e.target.value.length < 255 && this.setState({name: e.target.value})}/>
                </div>
            </div>;
        }
    }

    handleGeometryChange(value: GeometryChoice) {
        if (value === 'COPY') {
            if (this.selectedPointFeatureWktStrings().length > 0) {
                this.props.fetchWktFromSelection(this.selectedPointFeatureWktStrings());
            }
        }
        this.setState({selectedGeometryMethod: value});
    }

    renderGeometryChoice() {
        if (this.selectedObjectType() === null) {
            return null;
        } else if (this.props.createdWkt === null) {
            return <>
                <h3>Geometri</h3>
                {
                    Object.entries(geometryChoices)
                        .map(([key, text]) =>
                            <Radio
                                key={key}
                                disabled={(key === 'REFERENCE' && !this.selectedObjectType().geometryTypes.some(type => type === 'POINT' || type === 'LINE'))
                                    || (key === 'COPY' && (!this.selectedObjectType().geometryTypes.some(type => type === 'LINE' || type === 'MULTILINE') || this.selectedPointFeatureWktStrings().length === 0))}
                                checked={this.state.selectedGeometryMethod === key}
                                onChange={() => this.handleGeometryChange(key as GeometryChoice)}>{text(this.selectedPointFeatureWktStrings().length)}
                            </Radio>)
                }
            </>;
        } else {
            return <>
                {this.state.selectedGeometryMethod === 'COPY' && <p className={'content'}>NB: Om geometrien går frem og tilbake mellom punkter i et unaturlig mønster, fjern det nye objektet og prøv å velge ett og ett punkt i den rekkefølgen du ønsker geometrien skal følge.</p>}
                <div className={'row full-width'}>
                    <label>Geometri</label>
                    {this.state.referenceEnd === '' ?
                      <div className={'content'}>
                          <p>Hentet fra vegsystemreferanse</p>
                          <span className={'bold'}>{this.state.reference}</span>
                      </div>
                      :
                      <div className={'content'}>
                          <p>Hentet fra vegsystemreferanse med</p>
                          <p>startpunkt <span className={'bold'}>{this.state.reference}</span> og</p>
                          <p>sluttpunkt <span className={'bold'}>{this.state.referenceEnd}</span></p>
                      </div>
                    }
                      <div className={'action-icon align-bottom'} title={'Fjern geometri'} onClick={() => {
                          this.props.setWkt(null);
                          this.setState({selectedGeometryMethod: null, reference: '', referenceEnd: ''});
                      }}>
                          <i className={'fa fa-trash'}/>
                      </div>
                </div>
            </>;
        }
    }

    getPlaceholderFromExtentType(extentType: string): string[] {
        const pointExample = 'F.eks. EV6 S1D1 m15';
        const lineStartExample = 'F.eks. FV6690 S1D3 m642';
        const lineEndExample = 'F.eks. FV6690 S1D3 m809';

        if (extentType.includes('POINT')) {
            return [pointExample];
        } else {
            return [lineStartExample, lineEndExample];
        }
    }

    renderReferenceInput() {
        if (this.state.selectedGeometryMethod === 'REFERENCE' && this.selectedObjectType()) {
            const extentType = this.selectedObjectType().extentType;
            const placeholder = this.getPlaceholderFromExtentType(extentType);

            return <div>
                <h3>Vegsystemreferanse</h3>
                {extentType.includes('POINT') ?
                    <>
                        <div className={'row full-width'}>
                            <label htmlFor={'reference'}>Punkt</label>
                            <input value={this.state.reference} id={'reference'} placeholder={placeholder[0]} onChange={(e) => this.setState({reference: e.target.value})}/>
                        </div>
                        <div className={'row'}>
                            <Button className={'full-width'} disabled={this.state.reference === ''} onClick={() => this.props.fetchWktFromReference(this.state.reference)}>Hent geometri fra referanse</Button>
                        </div>
                    </>
                    :
                    <>
                        <div className={'row full-width'} >
                            <label htmlFor={'referenceStart'}>Startpunkt</label>
                            <input id={'referenceStart'} value={this.state.reference} placeholder={placeholder[0]} onChange={(e) => this.setState({reference: e.target.value})} />
                        </div>
                        <div className={'row full-width'}>
                            <label htmlFor={'referenceEnd'}>Sluttpunkt</label>
                            <input id={'referenceEnd'} value={this.state.referenceEnd} placeholder={placeholder[1]} onChange={(e) => this.setState({referenceEnd: e.target.value})}/>
                        </div>
                        <div className={'row'}>
                            <Button className={'full-width'} disabled={this.state.reference === '' || this.state.referenceEnd === ''} onClick={() => this.props.fetchWktFromReference(`${this.state.reference}-${this.state.referenceEnd}`)}>Hent geometri fra referanse</Button>
                        </div>
                    </>
                }
                {this.props.referenceError &&
                    <p className={'content error'}>Kunne ikke finne vegsystemreferansen. Har du skrevet rett?</p>}
            </div>;
        } else {
            return null;
        }
    }

    renderAssociateCheck() {
        if (this.state.selectedGeometryMethod === 'COPY' && this.props.user.hasPermission('ASSOCIATION:WRITE')) {
            const text = `Sett "${this.state.name}" som mor til ${this.selectedPointFeatureWktStrings().length} valgte objekter`;
            if (this.state.validRelationship === 'loading') {
                return <div>Sjekker om typene kan sammenkobles...</div>;
            }
            return  <Checkbox
                checked={this.state.associate}
                onChange={() => this.setState({associate: !this.state.associate})}
                disabled={this.selectedPointFeatureWktStrings().length === 0 || this.props.createObjectTypeId === null || this.props.createdWkt === null || this.state.validRelationship === false}
            >{text}</Checkbox>;
        } else {
            return null;
        }
    }

    renderActionButtons() {
        if (this.props.createdWkt === null && this.selectedObjectType() !== null
            && (this.state.selectedGeometryMethod === 'DRAW')) {
            const types = this.selectedObjectType()?.geometryTypes;
            return <>
                {types.includes('POINT') && <Button onClick={() => this.handleConfirmGeometryChoice('POINT')}>Tegn punkt</Button>}
                {types.includes('LINE') && <Button onClick={() => this.handleConfirmGeometryChoice('LINE')}>Tegn linje</Button>}
                {types.includes('POLYGON') && <Button onClick={() => this.handleConfirmGeometryChoice('POLYGON')}>Tegn polygon</Button>}
            </>;
        }
        let disabled = true;
        if (this.state.name.length > 0
            && this.state.validRelationship !== 'loading'
            && this.selectedObjectType() !== null
            && this.state.selectedGeometryMethod !== null
            && (this.state.selectedGeometryMethod !== 'REFERENCE' || this.props.createdWkt !== null)
            && (this.state.selectedGeometryMethod !== 'COPY' || this.selectedPointFeatureWktStrings().length > 0)) {
            disabled = false;
        }
        return <Button disabled={disabled} onClick={this.handleCreate}>Opprett</Button>;
    }

    render() {
        const {visible} = this.props;

        if (!visible) {
            return null;
        }
        return (
            <Draggable>
                <Modal className={'create-object-modal'} backdrop={'static'} onHide={this.clear} show={visible}>
                    <ModalHeader>
                        <h1>Opprett objekt</h1>
                    </ModalHeader>
                    <ModalBody>
                        <div>
                            {this.renderTypeChoice()}
                            {this.renderGeometryChoice()}
                            {this.renderReferenceInput()}
                        </div>
                    </ModalBody>
                    <ModalFooter className={'create-object-footer'}>
                        {
                            this.selectedObjectType() === null && <div className={'create-object-filter'}>
                                <input autoFocus className={'search'} placeholder={'Søk...'} onChange={(e) => this.setState({searchString: e.target.value})}/>
                            </div>
                        }
                        {this.renderAssociateCheck()}
                        <Button onClick={this.clear}>Lukk</Button>
                        {this.renderActionButtons()}
                    </ModalFooter>
                </Modal>
            </Draggable>
        );
    }
}

const mapStateToProps = (state: FeaturesState): CreateObjectStateProps => ({
    user: user(state),
    selectedFeatureType: selectedFeatureType(state),
    createObjectTypeId: createObjectTypeId(state),
    createdWkt: createdObjectWkt(state),
    visible: isCreateObjectsVisible(state),
    createGeometryType: creatingObjectGeometry(state),
    catalog: state.datacatalog,
    selectedFeatures: selectedFeatures(state),
    referenceError: stateReferenceError(state)
});

const mapDispatchToProps: CreateObjectDispatchProps = {
    createGeometry: setCreatingObjectGeometry,
    fetchWktFromReference: getWktFromReference,
    fetchWktFromSelection: getWktFromPoints,
    checkRelationship: checkValidRelationship,
    createFeature,
    setTypeId: setCreateObjectTypeId,
    setWkt: setCreateObjectWkt,
    hide: () => setCreateObjectVisible(false),
    setReferenseError: setReferenceError
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateObjectModal);
